import axiosInstance from '../utils/axios';

/**
 * 시스템 알림 관련 API 서비스
 * IA-08 요구사항에 따른 시스템 사용 알림 메시지 API 호출
 */
class SystemAlertService {
    
    /**
     * 알림 목록 조회
     * @param {Object} filters - 필터링 옵션
     * @param {number} page - 페이지 번호
     * @param {number} limit - 페이지당 항목 수
     * @returns {Promise<Object>} API 응답
     */
    async getAlerts(filters = {}, page = 1, limit = 20) {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value != null && value !== '')
                )
            });
            
            const response = await axiosInstance.get(`/system-alerts?${params}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 목록을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 목록 조회 에러:', error);
            const errorMessage = error.response?.data?.message || error.message || '알림 목록을 불러오는 중 오류가 발생했습니다.';
            return {
                success: false,
                message: errorMessage
            };
        }
    }

    /**
     * 알림 상세 조회
     * @param {string} alertId - 알림 ID
     * @returns {Promise<Object>} API 응답
     */
    async getAlert(alertId) {
        try {
            const response = await axiosInstance.get(`/system-alerts/${alertId}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 정보를 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 상세 조회 에러:', error);
            return {
                success: false,
                message: '알림 정보를 불러오는 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 유효한 알림 조회 (사용자 타입별)
     * @param {string} userType - 사용자 타입 (admin, user)
     * @returns {Promise<Object>} API 응답
     */
    async getEffectiveAlerts(userType = 'admin') {
        try {
            const response = await axiosInstance.get(`/system-alerts/effective/${userType}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '유효한 알림을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('유효한 알림 조회 에러:', error);
            return {
                success: false,
                message: '유효한 알림을 불러오는 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 생성
     * @param {Object} alertData - 알림 데이터
     * @returns {Promise<Object>} API 응답
     */
    async createAlert(alertData) {
        try {
            const response = await axiosInstance.post('/system-alerts', alertData);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '알림이 성공적으로 생성되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 생성에 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 생성 에러:', error);
            return {
                success: false,
                message: '알림 생성 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 수정
     * @param {string} alertId - 알림 ID
     * @param {Object} alertData - 수정할 알림 데이터
     * @returns {Promise<Object>} API 응답
     */
    async updateAlert(alertId, alertData) {
        try {
            const response = await axiosInstance.put(`/system-alerts/${alertId}`, alertData);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '알림이 성공적으로 수정되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 수정에 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 수정 에러:', error);
            return {
                success: false,
                message: '알림 수정 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 삭제
     * @param {string} alertId - 알림 ID
     * @returns {Promise<Object>} API 응답
     */
    async deleteAlert(alertId) {
        try {
            const response = await axiosInstance.delete(`/system-alerts/${alertId}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    message: response.data.message || '알림이 성공적으로 삭제되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 삭제에 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 삭제 에러:', error);
            return {
                success: false,
                message: '알림 삭제 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 활성화 상태 변경
     * @param {string} alertId - 알림 ID
     * @param {boolean} isActive - 활성화 상태
     * @returns {Promise<Object>} API 응답
     */
    async updateAlertStatus(alertId, isActive) {
        try {
            const response = await axiosInstance.patch(`/system-alerts/${alertId}/status`, {
                is_active: isActive
            });

            console.log(response);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '알림 상태가 성공적으로 변경되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 상태 변경에 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 상태 변경 에러:', error);
            return {
                success: false,
                message: '알림 상태 변경 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 통계 조회
     * @param {Object} params - 통계 조회 파라미터
     * @returns {Promise<Object>} API 응답
     */
    async getAlertStatistics(params = {}) {
        try {
            const queryParams = new URLSearchParams(params);
            const response = await axiosInstance.get(`/system-alerts/statistics?${queryParams}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 통계를 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 통계 조회 에러:', error);
            return {
                success: false,
                message: '알림 통계를 불러오는 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 확인 처리
     * @param {string} alertId - 알림 ID
     * @param {string} userId - 사용자 ID
     * @returns {Promise<Object>} API 응답
     */
    async acknowledgeAlert(alertId, userId) {
        try {
            const response = await axiosInstance.post(`/system-alerts/${alertId}/acknowledge`, {
                user_id: userId
            });
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data,
                    message: response.data.message || '알림이 성공적으로 확인되었습니다.'
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 확인 처리에 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 확인 처리 에러:', error);
            return {
                success: false,
                message: '알림 확인 처리 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 확인 이력 조회
     * @param {string} alertId - 알림 ID
     * @returns {Promise<Object>} API 응답
     */
    async getAcknowledgmentHistory(alertId) {
        try {
            const response = await axiosInstance.get(`/system-alerts/${alertId}/acknowledgments`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 확인 이력을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 확인 이력 조회 에러:', error);
            return {
                success: false,
                message: '알림 확인 이력을 불러오는 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 전체 알림 확인 이력 조회 (필터링 지원)
     * @param {Object} filters - 필터링 옵션
     * @param {number} page - 페이지 번호
     * @param {number} limit - 페이지당 항목 수
     * @returns {Promise<Object>} API 응답
     */
    async getAllAcknowledgmentHistory(filters = {}, page = 1, limit = 20) {
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString(),
                ...Object.fromEntries(
                    Object.entries(filters).filter(([_, value]) => value != null && value !== '')
                )
            });
            
            const response = await axiosInstance.get(`/system-alerts/acknowledgments?${params}`);
            
            if (response.data && response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }
            
            return {
                success: false,
                message: response.data.message || '알림 확인 이력을 불러오는데 실패했습니다.'
            };
        } catch (error) {
            console.error('알림 확인 이력 조회 에러:', error);
            return {
                success: false,
                message: '알림 확인 이력을 불러오는 중 오류가 발생했습니다.'
            };
        }
    }

    /**
     * 알림 우선순위 옵션 조회
     * @returns {Array} 우선순위 옵션
     */
    getPriorityOptions() {
        return [
            { value: 'high', label: '높음', variant: 'danger' },
            { value: 'medium', label: '보통', variant: 'warning' },
            { value: 'low', label: '낮음', variant: 'info' }
        ];
    }

    /**
     * 알림 타입 옵션 조회
     * @returns {Array} 알림 타입 옵션
     */
    getAlertTypeOptions() {
        return [
            { value: 'security', label: '보안', icon: 'shield-exclamation' },
            { value: 'maintenance', label: '유지보수', icon: 'tools' },
            { value: 'update', label: '업데이트', icon: 'arrow-up-circle' },
            { value: 'general', label: '일반', icon: 'info-circle' }
        ];
    }

    /**
     * 사용자 타입 옵션 조회
     * @returns {Array} 사용자 타입 옵션
     */
    getUserTypeOptions() {
        return [
            { value: 'admin', label: '관리자' },
            { value: 'user', label: '일반 사용자' },
            { value: 'all', label: '모든 사용자' }
        ];
    }
}

// 싱글톤 인스턴스 생성 및 내보내기
export const systemAlertService = new SystemAlertService();
export default systemAlertService;
